<template>
  <DialogForm v-if="user" ref="dialog" v-model:visible="visible" :form :loading :title="$t('edit-user')" @submit="submit">
    <VInput v-model="form.first_name" :label="$t('first-name')" :maxlength="100" prop="first_name" />
    <VInput v-model="form.last_name" :label="$t('last-name')" :maxlength="100" prop="last_name" />
    <VInput disabled :help="helpEmail" :label="$t('email')" :maxlength="255" :model-value="user.email" />

    <VSelect v-model="form.organization_function_id" clearable filterable :label="$t('function')">
      <VOption
        v-for="organizationFunction in $stores.organizationFunctions.functions"
        :key="organizationFunction.id"
        :label="organizationFunction.name"
        :value="organizationFunction.id"
      />
    </VSelect>

    <VSelect v-model="form.group_ids" clearable filterable :label="$t('groups')" multiple>
      <VOption v-for="group in $groups" :key="group.id" :label="group.name" :value="group.id" />
    </VSelect>

    <SelectRoles v-if="$can('userRoles.update')" v-model="form.roles" clearable prop="roles" />
    <SelectModuleRoles v-if="$can('userRoles.update') && $organization.has_transaction_monitoring" v-model="form.module_roles" prop="module_roles" />
    <VCheckbox v-model="form.reset_password" :label="$t('reset-password')" />
    <p v-show="form.reset_password">{{ $t('the-user-will-receive-an-email-with-an-activation-link-to-reset-the-password') }}</p>

    <template #footer>
      <VButton :label="$t('cancel')" type="secondary" @click="visible = false" />
      <VButton :label="$t('apply')" :loading @click="dialog?.submit()" />
    </template>

    <DialogForm v-model:visible="dialogAssignAlertsVisible" placement="center" :title="$t('remove-transaction-monitoring-module')" top="40vh" @update:visible="loading = false">
      <p class="font-medium mb-5">{{ $t('the-user-has-alerts-assigned-please-reassign-them') }}</p>

      <AssigneeSelect v-model="alertAssignee" :exclude-user-ids="new Set($stores.users.userIdsWithoutTransactionMonitoringPermission).add(user.id)" size="small" />
      <template #footer>
        <VButton :label="$t('cancel')" type="secondary" @click="dialogAssignAlertsVisible = false" />
        <VButton :label="$t('apply')" @click="submit" />
      </template>
    </DialogForm>
  </DialogForm>
</template>

<script setup lang="ts">
import { type Assignee } from '~/helpers/index.ts'
import { APP_MODULE_ROLES, APP_ROLES } from '~/stores/roles.ts'
import { type Alert, type AlertTemplate, type Batch, type SearchResponse, type User } from '~/types.ts'

interface Props {
  user?: User
}

const { user = undefined } = defineProps<Props>()

const visible = defineModel<boolean>('visible')

const bus = useBus()

const helpEmail = $t('you-need-to-send-a-support-request-if-you-want-to-change-the-email')

const dialog = useTemplateRef('dialog')
const loading = ref(false)
const dialogAssignAlertsVisible = ref(false)
const alertAssignee = ref<Assignee>({ type: 'user', value: null })
const form = useForm(
  {
    id: -1,
    first_name: '',
    last_name: '',
    email: '',
    organization_function_id: null as null | number,
    group_ids: [] as number[],
    reset_password: false,
    roles: [] as string[],
    module_roles: [] as string[],
  },
  {
    rules: {
      first_name: { required: true },
      last_name: { required: true },
      roles: { validator: validateRoles, message: $t('your-organization-must-have-at-least-one-owner') },
    },
  },
)

watch(visible, (visible) => (visible ? updateForm() : undefined))

function updateForm() {
  if (!user) {
    return
  }

  form.updateDefaults({
    id: user.id,
    first_name: user.first_name,
    last_name: user.last_name,
    organization_function_id: user.pivot.organization_function_id,
    reset_password: false,
    roles: user.roles.map((role) => role.name).filter((role) => APP_ROLES.has(role) && !APP_MODULE_ROLES.has(role)),
    module_roles: user.roles.map((role) => role.name).filter((role) => APP_MODULE_ROLES.has(role)),
    group_ids: user.groups.map((group) => group.id),
  })
}

async function submit() {
  if (!user || !form.$hasChanges) {
    visible.value = false

    return
  }

  if (!dialogAssignAlertsVisible.value && can('users.update')) {
    await $confirm({
      title: $t('update-user'),
      message: $t('if-you-proceed-with-this-action-the-user-email-will-be-updated-with-your-provided-changes', { email: user.email }),
    })
  }

  loading.value = true

  const { roles, module_roles: moduleRoles, organization_function_id: organizationFunctionId, ...partialUser } = form.data()
  if (can('userRoles.update')) {
    // If the Transaction monitoring role is being removed, check if the user has any alerts or alert templates assigned.
    if (user.roles.some((role) => role.name === 'Transaction monitoring') && !moduleRoles.includes('Transaction monitoring')) {
      if (!dialogAssignAlertsVisible.value) {
        try {
          const { data: alerts } = await api.post<SearchResponse<Alert>>('alerts/searches', { organization_id: [stores.organization.id], user_id: [user.id], per_page: 1 })
          if (alerts.meta.total > 0) {
            dialogAssignAlertsVisible.value = true

            return
          }
          const { data: alertTemplates } = await api.post<SearchResponse<AlertTemplate>>('alert-templates/searches', {
            organization_id: [stores.organization.id],
            user_id: [user.id],
            per_page: 1,
          })
          if (alertTemplates.meta.total > 0) {
            dialogAssignAlertsVisible.value = true

            return
          }
        } catch {
          loading.value = false

          return
        }
      } else {
        const [{ data: batch }] = await Promise.all([
          api.patch<Batch>('alerts', {
            filters: { user_id: [user.id] },
            form: alertAssignee.value.type === 'user' ? { user_id: alertAssignee.value.value } : { group_id: alertAssignee.value.value },
          }),
          api.patch('alert-templates', {
            filters: { user_id: [user.id] },
            form: alertAssignee.value.type === 'user' ? { user_id: alertAssignee.value.value } : { group_id: alertAssignee.value.value },
          }),
        ])
        bus.emit('batch.created', batch)
        dialogAssignAlertsVisible.value = false
        await nextTick()
        loading.value = true
      }
    }
  }

  try {
    if (Object.keys(partialUser).some((key) => form.$changedKeys.includes(key as keyof typeof partialUser))) {
      await stores.users.update(partialUser)
    }

    if (form.$changedKeys.includes('organization_function_id')) {
      await stores.users.updateFunction(user.id, organizationFunctionId)
    }

    if ((can('userRoles.update') && form.$changedKeys.includes('roles')) || form.$changedKeys.includes('module_roles')) {
      await stores.users.updateRoles({ id: form.id, roles: [...roles, ...moduleRoles] })
    }

    $message.success($t('the-user-email-has-been-updated', { email: user.email }))
  } catch {
    $message.error($t('api-error'))
  }

  loading.value = false
  visible.value = false
}

function validateRoles(roles: string[]) {
  const owners = Object.values(stores.users.users).filter((user) => user.roles.some((role) => role.name === 'Owner'))
  const isOwner = owners.some((owner) => owner.id === user?.id)
  if (isOwner && owners.length < 2 && !roles.includes('Owner')) {
    return false
  }

  return true
}
</script>
